/**
 * Admin → System Diagnostics
 * =========================
 * Live-troubleshooting tools that live INSIDE the admin console, so
 * Christopher can debug production issues (like the missing-password-reset-
 * email he flagged Feb 2026) without needing shell / log access to the pod.
 *
 * Currently exposed:
 *   • Email health — MSAL token acquisition + Azure App Registration coords
 *   • Send a real test email to any address, surfaces Graph errors verbatim
 *   • Recent email attempts log (sent / failed) with HTTP status + error body
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Envelope, Check, X as CloseIcon, Warning, ArrowClockwise, PaperPlaneTilt } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function Card({ title, subtitle, children, right }) {
  return (
    <section className="rounded-2xl bg-[#0e0e16] border border-white/[0.06] p-5">
      <header className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h2 className="text-white text-sm font-bold uppercase tracking-wider">{title}</h2>
          {subtitle && <p className="text-[#71717A] text-[11px] mt-0.5">{subtitle}</p>}
        </div>
        {right}
      </header>
      {children}
    </section>
  );
}

function KeyVal({ k, v, ok }) {
  return (
    <div className="flex items-center justify-between text-xs py-1.5 border-b border-white/[0.04] last:border-b-0" data-testid={`kv-${k}`}>
      <span className="text-[#a1a1aa]">{k}</span>
      <span className={`font-mono ${ok === true ? 'text-[#10B981]' : ok === false ? 'text-[#EF4444]' : 'text-white'}`}>
        {typeof v === 'boolean' ? (v ? 'yes' : 'no') : (v ?? '—')}
      </span>
    </div>
  );
}

export default function AdminDiagnostics() {
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testBusy, setTestBusy] = useState(false);
  const [recent, setRecent] = useState([]);
  const [recentLoading, setRecentLoading] = useState(false);

  const loadHealth = useCallback(async () => {
    setHealthLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin-console/email/health`, { withCredentials: true });
      setHealth(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to fetch email health');
    } finally {
      setHealthLoading(false);
    }
  }, []);

  const loadRecent = useCallback(async () => {
    setRecentLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin-console/email/recent?limit=25`, { withCredentials: true });
      setRecent(data?.logs || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to fetch email log');
    } finally {
      setRecentLoading(false);
    }
  }, []);

  useEffect(() => { loadHealth(); loadRecent(); }, [loadHealth, loadRecent]);

  const sendTest = async () => {
    if (!testTo.trim()) return;
    setTestBusy(true); setTestResult(null);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin-console/email/test`,
        { to: testTo.trim() },
        { withCredentials: true }
      );
      setTestResult(data);
      if (data.ok) {
        toast.success(`Test email queued to ${data.sent_to}`);
        loadRecent();
      } else {
        toast.error('Send failed — see error below');
      }
    } catch (e) {
      setTestResult({ ok: false, error: e?.response?.data?.detail || e.message });
      toast.error('Request failed');
    } finally {
      setTestBusy(false);
    }
  };

  return (
    <div className="space-y-4 max-w-3xl" data-testid="admin-diagnostics-page">
      <header>
        <h1 className="text-white text-xl font-bold">System Diagnostics</h1>
        <p className="text-[#71717A] text-xs mt-1">Real-time backend health checks. Read-only unless you send a test.</p>
      </header>

      <Card
        title="Email Health"
        subtitle="Microsoft Graph auth + Azure App Registration coordinates."
        right={
          <button onClick={loadHealth} disabled={healthLoading}
            className="text-[10px] px-2 py-1 rounded bg-white/5 text-[#a1a1aa] hover:bg-white/10 inline-flex items-center gap-1 disabled:opacity-40"
            data-testid="email-health-refresh"
          ><ArrowClockwise size={11} /> Refresh</button>
        }
      >
        {!health ? (
          <p className="text-[#71717A] text-xs">Loading…</p>
        ) : (
          <>
            <KeyVal k="Mailbox" v={health.mailbox} ok={!!health.mailbox} />
            <KeyVal k="Tenant ID (last 6)" v={health.tenant_id_last6} />
            <KeyVal k="Client ID (last 6)" v={health.client_id_last6} />
            <KeyVal k="Client Secret set" v={health.client_secret_set} ok={health.client_secret_set === true} />
            <KeyVal k="Client Secret length" v={health.client_secret_len} />
            <KeyVal k="MSAL token acquired" v={health.msal_token_acquired} ok={health.msal_token_acquired === true} />
            {health.msal_error && (
              <div className="mt-2 p-2 rounded bg-[#EF4444]/10 border border-[#EF4444]/30" data-testid="email-msal-error">
                <p className="text-[10px] uppercase text-[#EF4444] font-bold tracking-wider">MSAL error</p>
                <p className="text-white text-[11px] mt-1 font-mono break-all">{health.msal_error}</p>
              </div>
            )}
          </>
        )}
      </Card>

      <Card title="Send Test Email" subtitle="Sends a real message via Graph. Surfaces the exact error on failure.">
        <div className="flex gap-2">
          <input
            type="email"
            value={testTo}
            onChange={e => setTestTo(e.target.value)}
            placeholder="admin@example.com"
            className="flex-1 bg-[#0a0a12] border border-white/[0.08] rounded-lg px-3 py-2 text-white text-sm placeholder:text-[#444] focus:outline-none focus:border-[#9D4CDD]/40"
            data-testid="email-test-input"
          />
          <button
            onClick={sendTest}
            disabled={testBusy || !testTo.trim()}
            className="px-4 py-2 rounded-lg bg-[#9D4CDD] text-white text-xs font-semibold hover:bg-[#8B3DC7] disabled:opacity-40 inline-flex items-center gap-1.5"
            data-testid="email-test-send"
          >
            <PaperPlaneTilt size={12} weight="bold" /> {testBusy ? 'Sending…' : 'Send'}
          </button>
        </div>
        {testResult && (
          <div className={`mt-3 p-3 rounded-lg border text-[12px] ${testResult.ok ? 'bg-[#10B981]/10 border-[#10B981]/30 text-[#10B981]' : 'bg-[#EF4444]/10 border-[#EF4444]/30 text-[#EF4444]'}`} data-testid="email-test-result">
            {testResult.ok
              ? <><Check size={13} weight="bold" className="inline mr-1" />Delivered to {testResult.sent_to}</>
              : <><Warning size={13} weight="fill" className="inline mr-1" /><span className="font-mono text-[11px]">{testResult.error}</span></>
            }
          </div>
        )}
      </Card>

      <Card
        title="Recent Email Attempts"
        subtitle="Last 25 outbound emails (password resets, admin sends, tests)."
        right={
          <button onClick={loadRecent} disabled={recentLoading}
            className="text-[10px] px-2 py-1 rounded bg-white/5 text-[#a1a1aa] hover:bg-white/10 inline-flex items-center gap-1 disabled:opacity-40"
            data-testid="email-recent-refresh"
          ><ArrowClockwise size={11} /> Refresh</button>
        }
      >
        {recent.length === 0 ? (
          <p className="text-[#71717A] text-xs">
            {recentLoading ? 'Loading…' : 'No email attempts yet. Send a test above or trigger a password reset to populate.'}
          </p>
        ) : (
          <div className="space-y-1.5">
            {recent.map(r => {
              const ok = r.status === 'sent';
              return (
                <div key={r._id} className={`p-2.5 rounded-lg text-[11px] border ${ok ? 'bg-white/[0.02] border-white/[0.06]' : 'bg-[#EF4444]/5 border-[#EF4444]/25'}`} data-testid={`email-log-${r._id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className={`font-mono text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider font-bold ${ok ? 'bg-[#10B981]/15 text-[#10B981]' : 'bg-[#EF4444]/15 text-[#EF4444]'}`}>
                      {r.status}
                    </span>
                    <span className="text-[#666] text-[10px]">{r.at ? new Date(r.at).toLocaleString() : ''}</span>
                  </div>
                  <p className="text-white mt-1 truncate">{r.subject}</p>
                  <p className="text-[#71717A] text-[10px] truncate">to: {(r.to || []).join(', ')}</p>
                  {!ok && (
                    <p className="mt-1 font-mono text-[10px] text-[#EF4444] break-all">
                      HTTP {r.http_status}: {r.error_body}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <div className="rounded-xl bg-white/[0.02] border border-white/[0.04] p-4">
        <p className="text-[11px] text-[#71717A] leading-relaxed">
          <strong className="text-[#a1a1aa]">Debugging &ldquo;user didn&rsquo;t get their password-reset email&rdquo;:</strong><br />
          1. Hit <em>Refresh</em> on <em>Email Health</em>. All rows must be green — if MSAL token acquisition fails, rotate <code>MS_CLIENT_SECRET</code> in Azure and update the env var.<br />
          2. Send a test email to the same address the user reported missing. If it fails, the error body will tell you exactly why (invalid recipient, throttled, permissions).<br />
          3. Check <em>Recent Email Attempts</em>: if the user&rsquo;s reset appears with status <code>sent</code>, the issue is delivery-side (spam / SPF-DKIM-DMARC).
        </p>
      </div>
    </div>
  );
}
